import Button from '../Controls/Button';
import Disclosure from '../Disclosure';
import Popup from '../Controls/Popup';
import React, { Fragment } from 'react';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAbsoluteImagePath } from '@utils/dom';
import { Menu, Transition } from '@headlessui/react';
import { useAuth, useCart, useSession } from '@hooks';

type FooterLink = {
  name: string,
  url: string,
  target?: React.HTMLAttributeAnchorTarget
}

type FooterProps = {
  clientInfo?: {
    name: string,
    url: string
  },
  links?: FooterLink[]
};

const Footer: React.FC<FooterProps> = ({ clientInfo, links }) => {

  const { sessionId, authMode, partner } = useSession();
  const { user } = useAuth();
  const { cart } = useCart();

  return <footer className="tw-bg-gray-50">
    <div className='tw-container tw-mx-auto tw-py-4'>
      <div className='tw-flex tw-justify-between tw-text-sm'>
        <div>
          <ul>
            <li>
              <span className="tw-uppercase">
                © {new Date().getFullYear()} <a href="https://www.sequoiasoft.com/" target="_blank" rel="external" className="tw-text-primary-500 hover:tw-text-primary-400 tw-underline tw-transition-colors tw-duration-200 tw-ease-in-out">Sequoiasoft</a>
              </span>
            </li>
            <li>
              {
                clientInfo &&
                <a href={clientInfo.url} rel='external' target="_blank" className='tw-mr-1 tw-text-primary-500 hover:tw-text-primary-400 tw-underline tw-transition-colors tw-duration-200 tw-ease-in-out' title="www.resalys.com">
                  {clientInfo.name}
                </a>
              }
              <span>Tous droits réservés</span>
            </li>
            {
              process.env.REACT_APP_DEBUG === JSON.stringify(true) &&
              <Popup title='Debug infos' btnElement={(onClick) => <li><Button variant="primary" size="sm" shape="link" onClick={onClick}>Debug JSON</Button></li>}>
                <div className='tw-flex tw-flex-col tw-gap-4'>
                  <Disclosure title='Session Context'>
                    <code className='tw-block tw-overflow-auto'>
                      Session: {sessionId}
                    </code>
                    <code className='tw-block tw-overflow-auto'>
                      AuthMode: {authMode}
                    </code>
                    {
                      partner && <code className='tw-block tw-overflow-auto'>
                        Partner: {JSON.stringify(partner)}
                      </code>
                    }
                  </Disclosure>
                  <Disclosure title='User Context'>
                    <code className='tw-block tw-overflow-auto'>
                      {JSON.stringify(user)}
                    </code>
                  </Disclosure>
                  <Disclosure title='Cart Context'>
                    <code className='tw-block tw-overflow-auto'>
                      {JSON.stringify(cart)}
                    </code>
                  </Disclosure>
                </div>
              </Popup>
            }
          </ul>
        </div>
        <div>
          {
            links && <ul>
              {links.map((link, index) => {
                return <li key={`footer_link_${index}`} className="hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out">
                  <a href={link.url} rel='external' target={link.target ? link.target : "_blank"}>
                    {link.name}
                  </a>
                </li>
              })}
              <li>
                <MenuLanguage />
              </li>
            </ul>
          }
        </div>
      </div>
    </div>
  </footer>
};


const availableLanguages = process.env.REACT_APP_LANG?.split(",") ?? ["FR"]

const MenuLanguage: React.FC = () => {

  const { lang } = useSession();

  return <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
    <div>
      <Menu.Button className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-p-2 tw-text-sm tw-font-medium tw-bg-gray-200 tw-rounded focus:tw-outline-none">
        <div className='tw-flex tw-items-center tw-gap-2'>
          <img className={`tw-w-4 tw-h-4 tw-rounded`} src={getAbsoluteImagePath(`/assets/img/svg/flags/${lang.toLowerCase()}.svg`)}></img>
          <span>{lang.toUpperCase()}</span>
        </div>
        <FontAwesomeIcon icon={faChevronDown} />
      </Menu.Button>
    </div>
    <Transition
      as={Fragment}
      enter="tw-transition tw-ease-out tw-duration-100"
      enterFrom="tw-transform tw-opacity-0 tw-scale-95"
      enterTo="tw-transform tw-opacity-100 tw-scale-100"
      leave="tw-transition tw-ease-in tw-duration-75"
      leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
      leaveTo="tw-transform tw-opacity-0 tw-scale-95"
    >
      <Menu.Items className="tw--top-2 tw-transform tw--translate-y-full tw-absolute tw-right-0 tw-origin-top-right tw-bg-white tw-divide-y tw-divide-gray-100 tw-rounded-md tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none">
        <div className="">
          {
            availableLanguages.map((l) => {
              return <Menu.Item key={`lang_${l}`}>
                {({ active }) => (
                  <button type="button" onClick={() => undefined}
                    className={`${active ? 'tw-bg-gray-50 tw-text-primary-500' : ''
                      } tw-flex tw-rounded-t tw-items-center tw-w-full tw-px-2 tw-py-2 tw-text-sm tw-gap-1`}
                  >
                    <img className={`tw-w-4 tw-h-4 tw-rounded mr-2`} src={getAbsoluteImagePath(`/assets/img/svg/flags/${l.toLowerCase()}.svg`)}></img>
                    {l.toUpperCase()}
                  </button>
                )}
              </Menu.Item>
            })
          }
        </div>
      </Menu.Items>
    </Transition>
  </Menu>
}

export default Footer;
